import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import {
  administrationRouteApi,
  administrationSiteApi,
} from "api/immunization";
import { LATERALITIES, humanise } from "config/immunizationConstants";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import ExternalCodesField from "./common/ExternalCodesField";
import {
  validateCode,
  validateExternalCodes,
  validateRequired,
} from "./common/validation";

const AdministrationSiteModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [routes, setRoutes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      display: data?.display || "",
      description: data?.description || "",
      bodyRegion: data?.bodyRegion || "",
      laterality: data?.laterality || "NOT_APPLICABLE",
      applicableRouteCodes:
        Array.isArray(data?.applicableRouteCodes) ?
          data.applicableRouteCodes
        : [],
      externalCodes:
        Array.isArray(data?.externalCodes) ? data.externalCodes : [],
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  useEffect(() => {
    if (!open) return;
    administrationRouteApi
      .active()
      .then((res) => setRoutes(res?.data?.data || []))
      .catch(() => toast.error("Could not load administration routes"));
  }, [open]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(
    () =>
      validateCode(form.code, { isUpdate, label: "Site code" }) ||
      validateRequired(form.display, "Display name") ||
      validateExternalCodes(form.externalCodes),
    [form, isUpdate],
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      display: form.display.trim(),
      description: form.description?.trim() || "",
      bodyRegion: form.bodyRegion?.trim() || "",
      laterality: form.laterality,
      applicableRouteCodes: form.applicableRouteCodes,
      externalCodes: form.externalCodes,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await administrationSiteApi.update(recordId, payload);
        toast.success("Administration site updated successfully");
      } else {
        await administrationSiteApi.create({
          ...payload,
          code: form.code.trim(),
        });
        toast.success("Administration site created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} administration site`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Administration Site"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Site Code"
          helperText='e.g. "left deltoid" becomes LEFT_DELTOID'
        />
        <TextField
          label="Display Name"
          required
          fullWidth
          size="small"
          value={form.display || ""}
          onChange={(event) => set({ display: event.target.value })}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Body Region"
          fullWidth
          size="small"
          value={form.bodyRegion || ""}
          onChange={(event) => set({ bodyRegion: event.target.value })}
        />
        <TextField
          select
          label="Laterality"
          size="small"
          sx={{ minWidth: 180 }}
          value={form.laterality || "NOT_APPLICABLE"}
          onChange={(event) => set({ laterality: event.target.value })}
        >
          {LATERALITIES.map((option) => (
            <MenuItem key={option} value={option}>
              {humanise(option)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={routes}
        value={routes.filter((route) =>
          (form.applicableRouteCodes || []).includes(route.code),
        )}
        getOptionLabel={(option) => option?.display || ""}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        onChange={(event, next) =>
          set({ applicableRouteCodes: next.map((item) => item.code) })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Valid For Routes"
            helperText="Leave empty to allow any route. An administration recorded against a route not listed here is rejected."
          />
        )}
      />

      <ExternalCodesField
        value={form.externalCodes}
        onChange={(next) => set({ externalCodes: next })}
        disabled={saving}
      />
    </MasterModalShell>
  );
};

export default AdministrationSiteModal;
